import { apiFetch } from '../api/client';
import { downloadFileUrl, previewFileUrl } from '../fileBrowser/fileBrowserApi';
import { showErrorToast, showSuccessToast, showToast } from '../notifications/toast';
import { convertHeicBlobToJpeg, isHeicPath } from './heicConversion';
import { displayNameFromPath, fileExtension } from './mediaFileTypes';
import type { MediaKind } from './mediaFileTypes';
import { mediaViewerHref } from './mediaPreviewUrls';

export function downloadMediaFile(mountId: string, relativePath: string): void {
  if (!mountId || !relativePath) return;
  window.open(downloadFileUrl(mountId, relativePath), '_blank', 'noopener');
}

function absoluteMediaViewerUrl(kind: MediaKind, mountId: string, relativePath: string): string {
  return new URL(mediaViewerHref(kind, mountId, relativePath), window.location.origin).href;
}

function isGifPath(relativePath: string): boolean {
  return fileExtension(relativePath) === 'gif';
}

async function blobToClipboardPng(blob: Blob): Promise<void> {
  const bitmap = await createImageBitmap(blob);
  try {
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas not available.');
    ctx.drawImage(bitmap, 0, 0);
    const pngBlob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/png');
    });
    if (!pngBlob) throw new Error('Could not convert image.');
    await navigator.clipboard.write([new ClipboardItem({ 'image/png': pngBlob })]);
  } finally {
    bitmap.close();
  }
}

async function writeImageBlobToClipboard(blob: Blob, mime: string): Promise<void> {
  await navigator.clipboard.write([new ClipboardItem({ [mime]: blob })]);
}

export async function copyImageToClipboard(mountId: string, relativePath: string): Promise<void> {
  if (!mountId || !relativePath) return;

  if (!navigator.clipboard?.write) {
    showErrorToast('Copy is not supported in this browser.');
    return;
  }

  const preserveGif = isGifPath(relativePath);

  try {
    const res = await apiFetch(previewFileUrl(mountId, relativePath));
    if (!res.ok) {
      showErrorToast('Could not load the image for copying.');
      return;
    }

    let blob = await res.blob();
    if (!blob.size) {
      showErrorToast('Image is empty.');
      return;
    }

    if (isHeicPath(relativePath)) {
      try {
        blob = await convertHeicBlobToJpeg(blob);
      } catch {
        showErrorToast('Could not convert this HEIC image for copying.');
        return;
      }
    }

    const mime = preserveGif
      ? 'image/gif'
      : isHeicPath(relativePath)
        ? 'image/jpeg'
        : blob.type && blob.type.startsWith('image/')
          ? blob.type
          : 'image/png';

    const clipboardBlob =
      preserveGif && blob.type !== 'image/gif' ? new Blob([blob], { type: 'image/gif' }) : blob;

    try {
      await writeImageBlobToClipboard(clipboardBlob, mime);
      showSuccessToast(preserveGif ? 'GIF copied to clipboard.' : 'Image copied to clipboard.');
      return;
    } catch {
      if (preserveGif) {
        showToast(
          'This browser does not support copying animated GIFs to the clipboard (a common web limitation). Use Download for the full file.',
          'info',
          5500
        );
        return;
      }
      try {
        await blobToClipboardPng(blob);
        showSuccessToast('Image copied to clipboard.');
        return;
      } catch {
        showErrorToast('Could not copy this image to the clipboard.');
      }
    }
  } catch {
    showErrorToast('Could not copy this image to the clipboard.');
  }
}

export async function shareMediaViewerLink(
  kind: MediaKind,
  mountId: string,
  relativePath: string
): Promise<void> {
  if (!mountId || !relativePath) return;

  const url = absoluteMediaViewerUrl(kind, mountId, relativePath);
  const title = displayNameFromPath(relativePath);

  if (typeof navigator.share === 'function') {
    try {
      await navigator.share({ url, title });
      return;
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
    }
  }

  if (!navigator.clipboard?.writeText) {
    showErrorToast('Sharing is not supported in this browser.');
    return;
  }

  try {
    await navigator.clipboard.writeText(url);
    showSuccessToast('Link copied to clipboard.');
  } catch {
    showErrorToast('Could not copy the link.');
  }
}
